import React, { useCallback, useState, useEffect } from 'react';
import VehicleSelect from '../vehicle/VehicleSelect';
import CreditorForm from './CreditorForm';
import Fields from './CreditorFields';
import PolicyChange from '../PolicyChange';
import ChangedFields from '../ChangedFields';
import Conclusion from '../Conclusion';
import moment from 'moment';
import { statesByAbbreviation } from '../../../../States';

const AddCreditor = ({ actions, policy, effectiveDate, onCancel, onComplete, save, onError, setLoading, onSave, onExit, onClearRate, rate, setRate }) => {
	const [vehicle, setVehicle] = useState(null);
	const [data, setData] = useState({
		changedFields: [],
		creditorType: 0,
		creditorFirstName: '',
		creditorLastName: '',
		creditorBusinessName: '',
		creditorAddressHouseNumber: '',
		creditorAddressStreetName: '',
		creditorAddressApartmentNumber: '',
		creditorAddressPOBox: '',
		creditorAddressZip: '',
		creditorAddressCity: '',
		creditorAddressStateId: null,
		creditorAddressCounty: '',
	});

	const [isComplete, setIsComplete] = useState(false);
	const [addressState, setAddressState] = useState({ addressLoading: false, previousZip: null });

	useEffect(() => {
		if (save) {
			setData({ ...data, ...save });

			if (save.originalVehicle) {
				setVehicle(save.originalVehicle);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [save]);

	const onChange = useCallback(
		(_, { name, value }) => {
			setData({ ...data, [name]: value, changedFields: [...new Set([...data.changedFields, name])] });
		},
		[data]
	);

	const onAddressChange = useCallback(
		(changedData) => {
			const changedKeys = Object.keys(changedData);
			setData({ ...data, ...changedData, changedFields: [...new Set([...data.changedFields, ...changedKeys])] });
		},
		[data]
	);

	const onSaveAndExit = useCallback(() => {
		onSave({ ...data, originalVehicle: vehicle }, rate);
		onExit();
	}, [data, onExit, onSave, rate, vehicle]);

	const onEdit = useCallback(() => {
		onClearRate();
	}, [onClearRate]);

	const onRate = useCallback(async () => {
		try {
			setLoading('Generating rate');
			const result = await actions.rateCreditor({ policyId: policy.policyId, policyNumber: policy.policyNumber, endorsementPolicyImageNumber: rate?.policyImageNum, vehicleNum: vehicle.vehicleNum, effectiveDate, ...data });
			const _rate = {
				...result.response,
				email: {
					action: 'Added',
					vehicle,
					addressLine1: data.creditorBusinessName !== '' ? data.creditorBusinessName : `${data.creditorFirstName} ${data.creditorLastName}`,
					addressLine2: (`${data.creditorAddressHouseNumber} ${data.creditorAddressStreetName} ${data.creditorAddressApartmentNumber}`.trim() + (data.creditorAddressPOBox !== '' ? ` P.O. Box ${data.creditorAddressPOBox}` : '')).trim(),
					addressLine3: `${data.creditorAddressCity}, ${Fields.creditorAddressStateId.options()[data.creditorAddressStateId - Fields.creditorAddressStateId.base]} ${data.creditorAddressZip}`,
				},
			};

			setRate(_rate);
			await onSave({ ...data, originalVehicle: vehicle }, _rate);
			setLoading(null);
		} catch (error) {
			setLoading(null);
			onError(error.error);
		}
	}, [setLoading, actions, policy, rate, vehicle, effectiveDate, data, setRate, onSave, onError]);

	const onRateConfirm = useCallback(() => {
		onComplete(rate);
		setIsComplete(true);
	}, [setIsComplete, onComplete, rate]);

	const addressLookup = useCallback(
		async (zipCode) => {
			if (zipCode) {
				if (zipCode.length === 5 && zipCode !== addressState.previousZip) {
					try {
						setAddressState({ loadingAddress: true });
						const result = await actions.addressLookup(zipCode);

						if (result) {
							const state = statesByAbbreviation[result.response.stateAbbrev];

							if (data.creditorAddressStateId !== state ||
								data.creditorAddressCity !== result.response.city) {

                                const changedData = {
                                    creditorAddressStateId: state,
                                    creditorAddressCity: result.response.city
                                };
                                onAddressChange(changedData);
                            }
                        }
					} catch (error) {}
					setAddressState({ loadingAddress: false, previousZip: zipCode });
				}
			}
		},
		[actions, onAddressChange]
	);

	if (!vehicle) return <VehicleSelect policy={policy} onVehicleSelect={setVehicle} message="Please select the vehicle you're adding the creditor to" onCancel={onCancel} minimumCreditorsRequired={0} />;

	if (rate && !isComplete) {
		return (
			<PolicyChange name='Creditor' policy={policy} onComplete={onRateConfirm} onSaveAndExit={onSaveAndExit} onEdit={onEdit} onCancel={onCancel} title={`Please review and complete adding the creditor`} showEdit rate={rate} hideConfirm>
				<ChangedFields fields={Fields} changedFields={data.changedFields} data={data} isAdd other={[{ label: 'Effective Date of Change', text: `${moment(effectiveDate).format('L')} to ${moment(rate.policyExpirationDate).format('L')}` }]} />
			</PolicyChange>
		);
	}

	if (isComplete) {
		return (
			<Conclusion title='The change to your lienholder information has been completed successfully!' policy={policy} onExit={onExit} rate={rate}>
				<ChangedFields fields={Fields} changedFields={data.changedFields} data={data} isAdd other={[{ label: 'Effective Date of Change', text: `${moment(effectiveDate).format('L')} to ${moment(rate.policyExpirationDate).format('L')}` }]} />
			</Conclusion>
		);
	}

	return <CreditorForm data={data} onChange={onChange} vehicle={vehicle} onSaveAndExit={onSaveAndExit} onContinue={onRate} onCancel={onCancel} addressLookup={addressLookup} addressState={addressState} />;
};

export default AddCreditor;
